// src/features/profile/Profile.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Button, 
  Alert,
  Spinner,
  Modal
} from 'react-bootstrap';
import { 
  User, 
  Mail, 
  Phone, 
  Lock, 
  Save, 
  XCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Shield,
  Sparkles,
  Camera,
  LogOut,
  UserCheck,
  Clock,
  Calendar,
  Building2
} from 'lucide-react';
import { useAuth } from '../../context';
import { businessService } from '../../services';
import './Profile.css';

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Profile states
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [company_name, setcompany_name] = useState('');
  const [memberSince, setMemberSince] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // Modal states
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Load profile data
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const result = await businessService.getProfile();
      if (result.success) {
        const data = result.data;
        setFullName(data.full_name || '');
        setPhoneNumber(data.phone || '');
        setEmail(data.email || '');
        setcompany_name(data.company_name || '');
        setMemberSince(data.created_at ? new Date(data.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '');
      } else {
        setProfileError('Failed to load profile');
      }
    } catch (err) {
      setProfileError('Failed to load profile');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setProfileSuccess('');
    setProfileError('');

    try {
      const result = await businessService.updateProfile(fullName, phoneNumber);
      if (result.success) {
        setProfileSuccess('Profile updated successfully!');
        // Update user context
        const currentUser = { ...user, full_name: fullName, phone: phoneNumber };
        localStorage.setItem('golf_user', JSON.stringify(currentUser));
        setTimeout(() => setProfileSuccess(''), 5000);
      } else {
        setProfileError(result.message || 'Failed to update profile');
      }
    } catch (err) {
      setProfileError('Failed to update profile');
    }
    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    setPasswordSuccess('');
    setPasswordError('');

    try {
      const result = await businessService.changePassword(currentPassword, newPassword, confirmPassword);
      if (result.success) {
        setPasswordSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordSuccess(''), 5000);
      } else {
        setPasswordError(result.message || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('Failed to change password');
    }
    setPasswordLoading(false);
  };

  const handleLogout = async () => {
    setShowLogoutModal(false);
    await logout();
    navigate('/');
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    value = value.replace(/\s/g, '');
    if (value.startsWith('254') && !value.startsWith('+')) {
      value = '+' + value;
    }
    if (/^\+?[0-9]*$/.test(value) && value.length <= 13) {
      setPhoneNumber(value);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <div className="profile-header-left">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar">
                <span>{fullName?.[0]?.toUpperCase() || email?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <button className="profile-avatar-upload">
                <Camera size={14} color="white" />
              </button>
            </div>
            <div>
              <h1 className="profile-title">{fullName || 'User'}</h1>
              <p className="profile-subtitle">{email}</p>
            </div>
          </div>
          <Button 
            variant="danger" 
            className="profile-logout-btn"
            onClick={() => setShowLogoutModal(true)}
          >
            <LogOut size={16} />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="profile-stats-grid">
          <div className="profile-stat-card">
            <div className="profile-stat-icon" style={{ background: '#E0F2FE', color: '#0ea5e9' }}>
              <Calendar size={18} />
            </div>
            <div>
              <div className="profile-stat-label">Member Since</div>
              <div className="profile-stat-value">{memberSince || 'N/A'}</div>
            </div>
          </div>
          <div className="profile-stat-card">
            <div className="profile-stat-icon" style={{ background: '#D1FAE5', color: '#10B981' }}>
              <Building2 size={18} />
            </div>
            <div>
              <div className="profile-stat-label">Company name</div>
              <div className="profile-stat-value">{company_name || 'N/A'}</div>
            </div>
          </div>
          <div className="profile-stat-card">
            <div className="profile-stat-icon" style={{ background: '#EDE9FE', color: '#8B5CF6' }}>
              <UserCheck size={18} />
            </div>
            <div>
              <div className="profile-stat-label">Status</div>
              <div className="profile-stat-value" style={{ color: '#10B981' }}>Active</div>
            </div>
          </div>
        </div>

        <div className="profile-grid">
          {/* Profile Update Card */}
          <Card className="profile-card">
            <Card.Body>
              <div className="profile-card-header">
                <div className="profile-card-icon">
                  <User size={20} color="#0ea5e9" />
                </div>
                <div>
                  <h3 className="profile-card-title">Profile Information</h3>
                  <p className="profile-card-subtitle">Update your personal details</p>
                </div>
              </div>

              {/* Profile Alerts */}
              {profileSuccess && (
                <Alert className="profile-success-alert">
                  <CheckCircle size={18} />
                  {profileSuccess}
                </Alert>
              )}

              {profileError && (
                <Alert className="profile-error-alert">
                  <XCircle size={18} />
                  {profileError}
                </Alert>
              )}

              <Form onSubmit={handleProfileUpdate}>
                <div className="profile-form-row">
                  <div className="profile-form-group">
                    <Form.Label className="profile-form-label">
                      Full Name
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="profile-form-input"
                      required
                    />
                  </div>
                  <div className="profile-form-group">
                    <Form.Label className="profile-form-label">
                      Phone Number
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="0712345678 or +254712345678"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      className="profile-form-input"
                    />
                    <small className="profile-form-hint">
                      Must start with 07 or +254
                    </small>
                  </div>
                </div>

                <div className="profile-form-group">
                  <Form.Label className="profile-form-label">
                    Email Address
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    disabled
                    className="profile-form-input profile-form-input-disabled"
                  />
                  <small className="profile-form-hint">Email cannot be changed</small>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="profile-submit-btn"
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Save size={16} />
                      Save Changes
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          {/* Password Change Card */}
          <Card className="profile-card">
            <Card.Body>
              <div className="profile-card-header">
                <div className="profile-card-icon">
                  <Lock size={20} color="#0ea5e9" />
                </div>
                <div>
                  <h3 className="profile-card-title">Change Password</h3>
                  <p className="profile-card-subtitle">Update your security credentials</p>
                </div>
              </div>

              {/* Password Alerts */}
              {passwordSuccess && (
                <Alert className="profile-success-alert">
                  <CheckCircle size={18} />
                  {passwordSuccess}
                </Alert>
              )}

              {passwordError && (
                <Alert className="profile-error-alert">
                  <XCircle size={18} />
                  {passwordError}
                </Alert>
              )}

              <Form onSubmit={handlePasswordChange}>
                <div className="profile-form-group">
                  <Form.Label className="profile-form-label">
                    Current Password
                  </Form.Label>
                  <div className="profile-password-wrapper">
                    <Form.Control
                      type={showCurrentPassword ? 'text' : 'password'}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="profile-form-input profile-password-input"
                      required
                    />
                    <button
                      type="button"
                      className="profile-password-toggle"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="profile-form-group">
                  <Form.Label className="profile-form-label">
                    New Password
                  </Form.Label>
                  <div className="profile-password-wrapper">
                    <Form.Control
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password (min 8 chars)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="profile-form-input profile-password-input"
                      required
                    />
                    <button
                      type="button"
                      className="profile-password-toggle"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="profile-form-group">
                  <Form.Label className="profile-form-label">
                    Confirm New Password
                  </Form.Label>
                  <div className="profile-password-wrapper">
                    <Form.Control
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="profile-form-input profile-password-input"
                      required
                    />
                    <button
                      type="button"
                      className="profile-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={passwordLoading}
                  className="profile-submit-btn profile-password-btn"
                >
                  {passwordLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Lock size={16} />
                      Change Password
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </div>

        {/* Footer */}
        <div className="profile-footer">
          <span>
            <Shield size={14} />
            Secure profile management
          </span>
          <span>
            <Sparkles size={14} />
            Premium account
          </span>
          <span>
            <UserCheck size={14} />
            Verified user
          </span>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal 
        show={showLogoutModal} 
        onHide={() => setShowLogoutModal(false)}
        centered
        className="profile-logout-modal"
      >
        <Modal.Body className="profile-modal-body">
          <div className="profile-modal-icon">
            <LogOut size={32} color="#EF4444" />
          </div>
          <h4 className="profile-modal-title">Confirm Logout</h4>
          <p className="profile-modal-text">
            Are you sure you want to logout? You will need to login again to access your account.
          </p>
          <div className="profile-modal-actions">
            <Button 
              variant="secondary" 
              onClick={() => setShowLogoutModal(false)}
              className="profile-modal-cancel"
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleLogout}
              className="profile-modal-confirm"
            >
              Logout
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}